import { useCallback, useEffect, useRef, useState } from 'react';

export const useCountdown = (defaultDurationMs: number) => {
	const [minutes, setMinutes] = useState(0);
	const [seconds, setSeconds] = useState(0);
	const [isCountdown, setIsCountdown] = useState(false);
	const endTimestampRef = useRef<number | null>(null);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const updateFromRemaining = useCallback((remainingMs: number) => {
		const totalSec = Math.max(0, Math.floor(remainingMs / 1000));
		setMinutes(Math.floor(totalSec / 60));
		setSeconds(totalSec % 60);
	}, []);

	// Single interval: recompute remaining from endTimestamp each tick. No drift.
	useEffect(() => {
		if (!endTimestampRef.current) return;

		const tick = () => {
			const now = Date.now();
			const remaining = endTimestampRef.current! - now;
			if (remaining <= 0) {
				if (intervalRef.current) {
					clearInterval(intervalRef.current);
					intervalRef.current = null;
				}
				endTimestampRef.current = null;
				setMinutes(0);
				setSeconds(0);
				setIsCountdown(false);
				return;
			}
			updateFromRemaining(remaining);
		};

		tick();
		intervalRef.current = setInterval(tick, 1000);
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [updateFromRemaining, isCountdown]);

	/** Start countdown. If remainingMs is provided, resume from that remaining time; otherwise use full duration. */
	const handleStart = useCallback(
		(remainingMs?: number) => {
			const duration =
				remainingMs != null && remainingMs > 0
					? remainingMs
					: defaultDurationMs;
			endTimestampRef.current = Date.now() + duration;
			updateFromRemaining(duration);
			setIsCountdown(true);
		},
		[defaultDurationMs, updateFromRemaining]
	);

	const handleResetCountDown = useCallback(() => {
		endTimestampRef.current = null;
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
		const totalSec = Math.floor(defaultDurationMs / 1000);
		setMinutes(Math.floor(totalSec / 60));
		setSeconds(totalSec % 60);
		setIsCountdown(false);
	}, [defaultDurationMs]);

	return {
		minutes,
		seconds,
		handleStart,
		timer: `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
		isCountdown,
		handleResetCountDown,
	};
};
